<template>
  <BoxedFrame
    title="Expenses"
    class="expenses"
  >
    <section>
      <div class="section section-row section-header div-5">
        <div class="triple-f pl-3">
          Expense
        </div>
        <div class="double-f right-field">
          Amount
        </div>
      </div>

      <div>
        <template
          v-for="(row, index) in expenseCategoryRows"
          :key="row._formId"
        >
          <ExpenseCategoryRow
            :form-id="row._formId"
            :initial-values="(expenseCategories && expenseCategories[index]) || {}"
            :expenses="expenseCategories?.[index]?.expenses || []"
            :total="row.total"
            :disabled="loading"
            @save="onExpenseCategorySave(row._formId)"
          />
        </template>
      </div>
      <div class="section bold section-row div-5">
        <div class="triple-f">
          Total Expenses
        </div>
        <div class="right-field double-f">
          {{ formatCurrency(totalExpenses, { onZero: '-' }) }}
        </div>
      </div>
    </section>
  </BoxedFrame>
</template>

<script setup lang="ts">
import BoxedFrame from '@/components/BoxedFrame.vue';
import { computed } from 'vue';
import { useOfferSheetStore } from '@/stores/offer-sheet';
import { useOfferSheetFormStore } from '@/stores/offer-sheet-form';
import { useFormsStore } from 'vuniform';
import ExpenseCategoryRow from './ExpenseCategoryRow.vue';
import type { UpsertExpenseCategoryInput } from '@/generated/graphql';
import { formatCurrency } from '@/helpers/currency';
import { debounce } from 'debounce';

const offerSheetStore = useOfferSheetStore();
const offerSheetFormStore = useOfferSheetFormStore();
const formsStore = useFormsStore();

const expenseCategoryRows = computed(() => offerSheetFormStore.expenseCategoryRows);
const loading = computed(() => offerSheetStore.expensesLoading);
const expenseCategories = computed(() => offerSheetStore.offerSheet?.expenseCategories);
const expenseKeys = computed(() => offerSheetFormStore.expenseFormsKeys);
const totalExpenses = computed(() => offerSheetFormStore.totalExpenses);

const onExpenseCategorySave = debounce(async (formId: string) => {
  if (loading.value || !formsStore.isFormDirty(formId)) {
    return;
  }

  const payload = formsStore.formGetValues<UpsertExpenseCategoryInput>(formId);
  if (payload.id && !payload.name) {
    deleteCategory(formId, payload.id);
    return;
  }
  payload.offerSheetId = offerSheetStore.offerSheet.id;
  payload.clientMutationId = formId;

  const expenseCategory = await offerSheetStore.upsertExpenseCategory(payload);

  formsStore.SET_INITIAL_VALUES({
    values: { ...expenseCategory },
    formId,
    clearDirty: true,
  });

  formsStore.RESET_FIELD_VALUES(formId);
}, 1500);

const deleteCategory = async (formId: string, id: number) => {
  const hasExpenses = expenseKeys.value[formId].filter(k => formsStore.fieldGetValue(k, 'id')).length;

  if (hasExpenses > 0) {
    return;
  }

  await offerSheetStore.deleteExpenseCategory(id);

  formsStore.SET_INITIAL_VALUES({
    values: {},
    formId,
    clearDirty: true,
  });

  formsStore.RESET_FIELD_VALUES(formId);

  deleteRow(formId);
};

const deleteRow = formId => {
  offerSheetFormStore.deleteExpenseCategoryRow(formId);
};
</script>
